import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Info,
  Send,
  Trash2,
  X,
  XCircle,
} from "lucide-react";

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: Trash2,
  error: XCircle,
  send: Send,
  bell: Bell,
  trash: Trash2,
};

const TONES = {
  info: {
    bar: "from-sky-400 via-indigo-400 to-sky-500",
    glow: "bg-sky-400/25",
    iconWrapLight: "bg-sky-100 text-sky-600 ring-sky-200",
    iconWrapDark: "bg-sky-500/20 text-sky-200 ring-sky-400/30",
    confirmLight: "bg-sky-600 text-white hover:bg-sky-500",
    confirmDark: "bg-sky-500 text-white hover:bg-sky-400",
    chipLight: "bg-sky-50 text-sky-800 border-sky-200",
    chipDark: "bg-sky-500/10 text-sky-100 border-sky-400/20",
  },
  success: {
    bar: "from-emerald-400 via-teal-400 to-emerald-500",
    glow: "bg-emerald-400/25",
    iconWrapLight: "bg-emerald-100 text-emerald-600 ring-emerald-200",
    iconWrapDark: "bg-emerald-500/20 text-emerald-200 ring-emerald-400/30",
    confirmLight: "bg-emerald-600 text-white hover:bg-emerald-500",
    confirmDark: "bg-emerald-500 text-white hover:bg-emerald-400",
    chipLight: "bg-emerald-50 text-emerald-800 border-emerald-200",
    chipDark: "bg-emerald-500/10 text-emerald-100 border-emerald-400/20",
  },
  warning: {
    bar: "from-amber-400 via-orange-400 to-amber-500",
    glow: "bg-amber-400/30",
    iconWrapLight: "bg-amber-100 text-amber-600 ring-amber-200",
    iconWrapDark: "bg-amber-500/20 text-amber-200 ring-amber-400/30",
    confirmLight: "bg-amber-500 text-white hover:bg-amber-400",
    confirmDark: "bg-amber-500 text-gray-950 hover:bg-amber-400",
    chipLight: "bg-amber-50 text-amber-900 border-amber-200",
    chipDark: "bg-amber-500/10 text-amber-100 border-amber-400/20",
  },
  send: {
    bar: "from-amber-400 via-orange-400 to-amber-500",
    glow: "bg-amber-400/30",
    iconWrapLight: "bg-amber-100 text-amber-600 ring-amber-200",
    iconWrapDark: "bg-amber-500/20 text-amber-200 ring-amber-400/30",
    confirmLight: "bg-amber-500 text-white hover:bg-amber-400",
    confirmDark: "bg-amber-500 text-gray-950 hover:bg-amber-400",
    chipLight: "bg-amber-50 text-amber-900 border-amber-200",
    chipDark: "bg-amber-500/10 text-amber-100 border-amber-400/20",
  },
  danger: {
    bar: "from-rose-500 via-red-500 to-rose-600",
    glow: "bg-rose-500/25",
    iconWrapLight: "bg-rose-100 text-rose-600 ring-rose-200",
    iconWrapDark: "bg-rose-500/20 text-rose-200 ring-rose-400/30",
    confirmLight: "bg-rose-600 text-white hover:bg-rose-500",
    confirmDark: "bg-rose-500 text-white hover:bg-rose-400",
    chipLight: "bg-rose-50 text-rose-800 border-rose-200",
    chipDark: "bg-rose-500/10 text-rose-100 border-rose-400/20",
  },
  error: {
    bar: "from-rose-500 via-red-500 to-rose-600",
    glow: "bg-rose-500/25",
    iconWrapLight: "bg-rose-100 text-rose-600 ring-rose-200",
    iconWrapDark: "bg-rose-500/20 text-rose-200 ring-rose-400/30",
    confirmLight: "bg-rose-600 text-white hover:bg-rose-500",
    confirmDark: "bg-rose-500 text-white hover:bg-rose-400",
    chipLight: "bg-rose-50 text-rose-800 border-rose-200",
    chipDark: "bg-rose-500/10 text-rose-100 border-rose-400/20",
  },
};

/**
 * Themed notice / confirm dialog used instead of window.alert / window.confirm.
 */
export default function AppNoticeDialog({
  open = false,
  isDarkMode = false,
  mode = "alert",
  variant = "info",
  icon,
  title,
  message,
  highlights = [],
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy = false,
}) {
  const { t } = useTranslation();
  const tone = TONES[variant] || TONES.info;
  const Icon = ICONS[icon] || ICONS[variant] || Info;
  const isConfirm = mode === "confirm";
  const closeLabel = cancelLabel || t("common.close");
  const actionLabel = confirmLabel || t("common.ok");

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onCancel]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[1300] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
        >
          <button
            type="button"
            aria-label={closeLabel}
            disabled={busy}
            onClick={() => onCancel?.()}
            className="absolute inset-0 bg-black/60 backdrop-blur-[3px]"
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="app-notice-title"
            aria-describedby={message ? "app-notice-message" : undefined}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
            className={`relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-white/10 bg-gray-900 text-gray-100"
                : "border-gray-200 bg-white text-gray-900"
            }`}
          >
            <div className={`h-1.5 bg-gradient-to-r ${tone.bar}`} />
            <div className={`pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full blur-3xl ${tone.glow}`} />

            <button
              type="button"
              onClick={() => onCancel?.()}
              disabled={busy}
              className={`absolute right-3 top-3 rounded-lg p-1.5 transition ${
                isDarkMode
                  ? "text-gray-400 hover:bg-white/5 hover:text-gray-200"
                  : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              }`}
              aria-label={closeLabel}
            >
              <X size={16} />
            </button>

            <div className="relative px-6 pb-5 pt-6">
              <div
                className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${
                  isDarkMode ? tone.iconWrapDark : tone.iconWrapLight
                }`}
              >
                <Icon size={22} strokeWidth={2.2} />
              </div>

              {title ? (
                <h3 id="app-notice-title" className="pr-8 text-lg font-semibold leading-snug">
                  {title}
                </h3>
              ) : null}

              {message ? (
                <p
                  id="app-notice-message"
                  className={`mt-2 text-sm leading-relaxed ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {message}
                </p>
              ) : null}

              {highlights.length > 0 ? (
                <div className={`mt-4 grid gap-2 ${highlights.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {highlights.map((item) => (
                    <div
                      key={`${item.label}-${item.value}`}
                      className={`rounded-xl border px-3 py-2.5 ${
                        isDarkMode ? tone.chipDark : tone.chipLight
                      }`}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                        {item.label}
                      </p>
                      <p className="mt-0.5 text-lg font-semibold tabular-nums">{item.value}</p>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {isConfirm ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onCancel?.()}
                    className={`rounded-xl px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 ${
                      isDarkMode
                        ? "bg-white/5 text-gray-200 hover:bg-white/10"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {closeLabel}
                  </button>
                ) : null}
                <button
                  type="button"
                  autoFocus
                  disabled={busy}
                  onClick={() => onConfirm?.()}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition disabled:opacity-50 ${
                    isDarkMode ? tone.confirmDark : tone.confirmLight
                  }`}
                >
                  {actionLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
